import Link from "next/link";
import type { ReactNode } from "react";
import { cn } from "./cn";

export interface EntityLinkProps {
  href: string;
  /** Primary name of the record (user, client, unit, profile…). */
  children: string;
  /** Secondary line under the name (e-mail, código, razão social). */
  meta?: ReactNode;
  /** Leading visual: `"initials"` builds an avatar from the name; or pass an icon. */
  leading?: "initials" | ReactNode;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * Primary cell of a list row (padrão ASV). The name itself is the link — no
 * underline at rest; hover/focus reveal it's navigable. Never make the whole
 * row clickable (breaks text selection/copy) — see docs/ui/UX_PATTERNS.md.
 */
export function EntityLink({ href, children, meta, leading }: EntityLinkProps) {
  return (
    <div className="ui-entity">
      {leading === "initials" ? (
        <span className="ui-entity-avatar" aria-hidden="true">
          {initials(children)}
        </span>
      ) : leading ? (
        <span className={cn("ui-entity-avatar", "is-icon")} aria-hidden="true">
          {leading}
        </span>
      ) : null}
      <div className="ui-entity-text">
        <Link href={href} className="ui-entity-link">
          {children}
        </Link>
        {meta ? <span className="ui-entity-meta">{meta}</span> : null}
      </div>
    </div>
  );
}
